import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { COMPANIES } from "@/data/content";

const spanFor = (i) =>
  [
    "lg:col-span-2 lg:row-span-2",
    "lg:col-span-2",
    "lg:col-span-1",
    "lg:col-span-1",
    "lg:col-span-2",
    "lg:col-span-2",
  ][i];

export default function InnovationShowcase() {
  return (
    <section
      id="showcase"
      data-testid="innovation-showcase"
      className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6"
    >
      <Reveal>
        <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Eyebrow>Innovation Showcase</Eyebrow>
            <SectionTitle className="mt-3">
              Companies Driving <span className="text-brand">Innovation</span>
            </SectionTitle>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-mute">
            Educational profiles of the firms reshaping technology, energy, and
            global commerce. Featured for study purposes only.
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[280px]">
        {COMPANIES.map((c, i) => (
          <Reveal key={c.ticker} delay={i * 0.07} className={spanFor(i)}>
            <motion.article
              data-testid={`company-card-${c.ticker.toLowerCase()}`}
              whileHover="hover"
              className="card-trace group relative flex h-full min-h-[280px] flex-col justify-end overflow-hidden rounded-xl border border-line bg-panel"
            >
              <motion.img
                src={c.image}
                alt={`${c.name} — ${c.category}`}
                loading="lazy"
                variants={{ hover: { scale: 1.07 } }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="clip-frame absolute inset-0 h-full w-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/10" />

              <div className="absolute left-4 top-4 flex items-center gap-2">
                <span className="rounded-full border border-white/20 bg-black/50 px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] text-white backdrop-blur-sm">
                  {c.ticker}
                </span>
                <span className="rounded-full bg-brand px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-white">
                  {c.badge}
                </span>
              </div>
              <div className="absolute right-4 top-4 flex items-center gap-1 font-mono text-[11px] font-bold text-up">
                <TrendingUp size={12} />
                {c.trend}
              </div>

              <div className="relative p-5 text-left">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">
                  {c.category}
                </p>
                <h3 className="mt-1.5 flex items-center gap-1.5 font-display text-2xl font-bold uppercase tracking-tight text-white">
                  {c.name}
                  <ArrowUpRight
                    size={17}
                    className="text-brand opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  />
                </h3>
                <p
                  className={`mt-2 text-xs leading-relaxed text-neutral-300 sm:text-sm ${
                    i > 0 ? "line-clamp-3" : ""
                  }`}
                >
                  {c.summary}
                </p>
                <div className="mt-3.5 flex items-center justify-between border-t border-white/10 pt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400">
                  <span>Focus: {c.focus}</span>
                  <span className="text-white/70">MCap {c.marketCap}</span>
                </div>
              </div>
            </motion.article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <p className="mt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-dim">
          Companies featured solely for informational and educational purposes.
          TEAM X is not affiliated with, endorsed by, or sponsored by any
          featured company.
        </p>
      </Reveal>
    </section>
  );
}
